/*
 * Copyright (C) 2018 - 2026 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import React from 'react';
import 'antd/dist/reset.css';
import axios from 'axios';
import { Result } from 'antd';
import ConfigContext from './components/ConfigContext';
import { ThemeProvider } from './components/ThemeContext';
import { withTranslation } from 'react-i18next';
import PageLoading from './components/PageLoading';
import { randomizeImageUrlToAvoidLoadingFromCache } from './services/utils/urlUtils';
import RouteWithSubRoutes from './components/RouteWithSubRoutes';
import { BrowserRouter as Router, Switch } from 'react-router-dom';

const errorView = (
  <Result
    status="500"
    title={'Error occurred while loading the configuration'}
    subTitle={'Please refresh your browser window'}
  />
);

class App extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      error: false,
      config: {},
    };
  }

  componentDidMount() {
    // Clear cache related to billing feature
    localStorage.removeItem('start');
    localStorage.removeItem('end');
    localStorage.removeItem('tenant');
    axios
      .get(
        window.location.origin +
          `/${window.location.pathname.split('/')[1]}/public/conf/config.json`,
      )
      .then((res) => {
        const config = res.data;
        this.setState({
          config: config,
          loading: false,
          error: false,
        });
        this.initFavicon();
      })
      .catch((error) => {
        this.setState({
          loading: false,
          error: true,
        });
      });
  }

  initFavicon = () => {
    const faviconUrl = `${window.location.origin}/${window.location.pathname.split('/')[1]}/public/images/favicon.png`;
    this.updateFavicon(faviconUrl);
  };

  updateFavicon = (faviconUrl) => {
    const link =
      document.querySelector("link[rel*='icon']") ||
      document.createElement('link');
    link.type = 'image/x-icon';
    link.rel = 'shortcut icon';
    link.href = randomizeImageUrlToAvoidLoadingFromCache(faviconUrl);
    document.getElementsByTagName('head')[0].appendChild(link);
  };

  render() {
    const { loading, error, config } = this.state;

    return (
      <div className="root-container">
        {loading ? (
          <PageLoading
            fullPage
            message={this.props.t('label_loading_subtitle')}
          />
        ) : error ? (
          errorView
        ) : (
          <ThemeProvider>
            <Router>
              <ConfigContext.Provider
                value={{
                  config,
                }}
              >
                <div className="App">
                  <Switch>
                    {this.props.routes.map((route) => (
                      <RouteWithSubRoutes
                        key={route.path}
                        {...route}
                        defaultAppTitle={config.defaultAppTitle}
                      />
                    ))}
                  </Switch>
                </div>
              </ConfigContext.Provider>
            </Router>
          </ThemeProvider>
        )}
      </div>
    );
  }
}

export default withTranslation()(App);
